import { useState, useCallback, useEffect } from 'react';
import { Sparkles, Globe, AlertCircle } from 'lucide-react';
import { useProjectStore } from '../../store/projectStore';
import { TextInput, TextArea, Select } from '../ui/FormField';
import { validateUrl, validateWhatsapp } from '../../lib/validators';
import { generateId } from '../../utils/helpers';
import { PROJECT_TEMPLATES, getProjectTemplate } from '../../lib/templateEngine';
import type {
  Archetype,
  Industry,
  ReferenceSite,
  DesignStyle,
  ColorScheme,
  FontPair,
  LayoutStyle,
} from '../../types/design-spec';

const INDUSTRIES: { value: Industry; label: string }[] = [
  { value: 'property', label: '房地產' },
  { value: 'medical', label: '醫療' },
  { value: 'restaurant', label: '餐飲' },
  { value: 'retail', label: '零售' },
  { value: 'consulting', label: '顧問諮詢' },
  { value: 'education', label: '教育' },
  { value: 'technology', label: '科技' },
  { value: 'finance', label: '金融' },
  { value: 'legal', label: '法律' },
  { value: 'creative', label: '創意' },
  { value: 'nonprofit', label: '非牟利' },
  { value: 'other', label: '其他' },
];

const ARCHETYPES: { value: Archetype; label: string }[] = [
  { value: 'landing', label: 'Landing Page' },
  { value: 'cms', label: 'CMS 內容管理' },
];

const DESIGN_STYLES: { value: DesignStyle; label: string }[] = [
  { value: 'modern-minimalist', label: 'Modern Minimalist' },
  { value: 'corporate-professional', label: 'Corporate Professional' },
  { value: 'creative-bold', label: 'Creative Bold' },
  { value: 'elegant-luxury', label: 'Elegant Luxury' },
  { value: 'tech-startup', label: 'Tech Startup' },
  { value: 'warm-friendly', label: 'Warm Friendly' },
  { value: 'medical-trusted', label: 'Medical Trusted' },
  { value: 'portfolio-artistic', label: 'Portfolio Artistic' },
  { value: 'educational-clean', label: 'Educational Clean' },
  { value: 'custom', label: 'Custom' },
];

const COLOR_SCHEMES: { value: ColorScheme; label: string }[] = [
  { value: 'blue-professional', label: 'Blue Professional' },
  { value: 'green-nature', label: 'Green Nature' },
  { value: 'red-energetic', label: 'Red Energetic' },
  { value: 'black-luxury', label: 'Black Luxury' },
  { value: 'warm-neutral', label: 'Warm Neutral' },
  { value: 'high-contrast', label: 'High Contrast' },
  { value: 'pastel-soft', label: 'Pastel Soft' },
  { value: 'custom', label: 'Custom' },
];

const FONT_PAIRS: { value: FontPair; label: string }[] = [
  { value: 'inter-noto', label: 'Inter + Noto Sans TC' },
  { value: 'playfair-inter', label: 'Playfair Display + Inter' },
  { value: 'montserrat-open', label: 'Montserrat + Open Sans' },
  { value: 'noto-tc-only', label: 'Noto Sans TC' },
  { value: 'system', label: 'System Fonts' },
  { value: 'custom', label: 'Custom' },
];

const LAYOUT_STYLES: { value: LayoutStyle; label: string }[] = [
  { value: 'centered-single', label: 'Centered Single' },
  { value: 'wide-multi', label: 'Wide Multi' },
  { value: 'asymmetric', label: 'Asymmetric' },
  { value: 'card-grid', label: 'Card Grid' },
  { value: 'editorial', label: 'Editorial' },
  { value: 'split', label: 'Split' },
  { value: 'custom', label: 'Custom' },
];

const PROJECT_TEMPLATE_OPTIONS = [
  { value: '', label: '通用模板（依 Archetype）' },
  ...PROJECT_TEMPLATES.map((t) => ({ value: t.id, label: t.name })),
];

export function Start() {
  const config = useProjectStore((state) => state.config);
  const setConfig = useProjectStore((state) => state.setConfig);
  const analyzeReferences = useProjectStore((state) => state.analyzeReferences);
  const analysisLoading = useProjectStore((state) => state.analysisLoading);
  const analysisError = useProjectStore((state) => state.analysisError);

  const [urls, setUrls] = useState<string[]>(
    config.references.map((r) => r.url).concat(['', '', '', '', '']).slice(0, 5)
  );
  const [templateId, setTemplateId] = useState<string>(config.templateId ?? '');
  const [industry, setIndustry] = useState<Industry>(config.industry);
  const [archetype, setArchetype] = useState<Archetype>(config.archetype);
  const [brandName, setBrandName] = useState(config.brand.nameCn || config.brand.nameEn || '');
  const [whatsapp, setWhatsapp] = useState(config.contact.whatsapp[0] || '');
  const [description, setDescription] = useState(config.brand.description || '');
  const [designStyle, setDesignStyle] = useState<DesignStyle>(
    config.stylePreferences?.designStyle ?? 'modern-minimalist'
  );
  const [colorScheme, setColorScheme] = useState<ColorScheme>(
    config.stylePreferences?.colorScheme ?? 'blue-professional'
  );
  const [fontPair, setFontPair] = useState<FontPair>(
    config.stylePreferences?.fontPair ?? 'inter-noto'
  );
  const [layoutStyle, setLayoutStyle] = useState<LayoutStyle>(
    config.stylePreferences?.layoutStyle ?? 'centered-single'
  );
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  // 選擇專案模板時，自動鎖定對應行業與 archetype
  useEffect(() => {
    if (!templateId) return;
    const template = getProjectTemplate(templateId);
    if (template) {
      setIndustry(template.industry);
      setArchetype(template.archetype);
    }
  }, [templateId]);

  const isTemplateLocked = Boolean(templateId);

  const updateUrl = (index: number, value: string) => {
    const next = [...urls];
    next[index] = value;
    setUrls(next);
  };

  const validate = useCallback((): boolean => {
    const errors: Record<string, string> = {};
    const filledUrls = urls.map((u) => u.trim()).filter(Boolean);

    if (filledUrls.length < 1) {
      errors.urls = '請至少輸入 1 個參考網站 URL';
    } else if (filledUrls.length > 5) {
      errors.urls = '最多可輸入 5 個參考網站 URL';
    } else {
      const invalid = filledUrls.filter((u) => !validateUrl(u));
      if (invalid.length > 0) {
        errors.urls = `無效的 URL: ${invalid.join(', ')}`;
      }
    }

    if (!brandName.trim()) {
      errors.brandName = '請輸入品牌名稱';
    }

    if (whatsapp.trim() && !validateWhatsapp(whatsapp.trim())) {
      errors.whatsapp = '請輸入有效的 WhatsApp 號碼（例如 +85212345678）';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  }, [urls, brandName, whatsapp]);

  const handleSubmit = async () => {
    if (!validate()) return;

    const filledUrls = urls.map((u) => u.trim()).filter(Boolean);
    const refs: ReferenceSite[] = filledUrls.map((url) => ({ id: generateId(), url }));

    const stylePreferences = {
      designStyle,
      colorScheme,
      fontPair,
      layoutStyle,
    };

    setConfig({
      templateId: templateId || undefined,
      archetype,
      industry,
      displayName: brandName.trim() || config.displayName,
      brand: {
        ...config.brand,
        nameEn: brandName.trim(),
        nameCn: brandName.trim(),
        description: description.trim(),
      },
      contact: {
        ...config.contact,
        whatsapp: whatsapp.trim() ? [whatsapp.trim()] : [],
      },
      references: refs,
      stylePreferences,
    });

    await analyzeReferences({
      urls: filledUrls,
      industry,
      archetype,
      brandInfo: {
        name: brandName.trim(),
        description: description.trim(),
      },
      stylePreferences,
    });
  };

  return (
    <div className="max-w-2xl mx-auto pb-10">
      <div className="text-center mb-8">
        <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 mb-4">
          <Globe size={28} />
        </div>
        <h2 className="text-3xl font-bold text-slate-900 dark:text-white mb-2">
          開始建立您的網站
        </h2>
        <p className="text-slate-600 dark:text-slate-300">
          選擇基礎模板、風格偏好，並提供參考網站，AI 會為您生成完整的網站方案。
        </p>
      </div>

      <div className="relative bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-6 md:p-8 shadow-sm">
        <div className="space-y-5">
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-2">
              參考網站 <span className="text-slate-400 font-normal">（1-5 個）</span>
            </label>
            <div className="space-y-3">
              {urls.map((url, index) => (
                <TextInput
                  key={index}
                  label=""
                  value={url}
                  onChange={(e) => updateUrl(index, e.target.value)}
                  placeholder="https://example.com"
                  disabled={analysisLoading}
                />
              ))}
            </div>
            {validationErrors.urls && (
              <p className="mt-2 text-sm text-red-500 flex items-center gap-1">
                <AlertCircle size={14} />
                {validationErrors.urls}
              </p>
            )}
          </div>

          <Select
            label="基礎模板"
            value={templateId}
            onChange={(e) => setTemplateId(e.target.value)}
            options={PROJECT_TEMPLATE_OPTIONS}
            disabled={analysisLoading}
            hint="選擇範例專案會自動鎖定對應行業與網站類型"
          />

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Select
              label="行業"
              value={industry}
              onChange={(e) => setIndustry(e.target.value as Industry)}
              options={INDUSTRIES}
              disabled={analysisLoading || isTemplateLocked}
              hint={isTemplateLocked ? '已由模板鎖定' : undefined}
            />
            <Select
              label="網站類型"
              value={archetype}
              onChange={(e) => setArchetype(e.target.value as Archetype)}
              options={ARCHETYPES}
              disabled={analysisLoading || isTemplateLocked}
              hint={isTemplateLocked ? '已由模板鎖定' : undefined}
            />
          </div>

          <TextInput
            label="品牌名稱"
            value={brandName}
            onChange={(e) => setBrandName(e.target.value)}
            error={validationErrors.brandName}
            required
            disabled={analysisLoading}
          />

          <TextInput
            label="WhatsApp（可選）"
            value={whatsapp}
            onChange={(e) => setWhatsapp(e.target.value)}
            error={validationErrors.whatsapp}
            placeholder="+85212345678"
            disabled={analysisLoading}
          />

          <TextArea
            label="業務簡介"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="請簡單描述您的業務，讓 AI 更了解您的需求..."
            rows={4}
            disabled={analysisLoading}
          />

          <div className="p-4 rounded-lg bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-700">
            <h3 className="text-sm font-semibold text-slate-900 dark:text-white mb-3">
              風格偏好
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Select
                label="設計風格"
                value={designStyle}
                onChange={(e) => setDesignStyle(e.target.value as DesignStyle)}
                options={DESIGN_STYLES}
                disabled={analysisLoading}
              />
              <Select
                label="配色方案"
                value={colorScheme}
                onChange={(e) => setColorScheme(e.target.value as ColorScheme)}
                options={COLOR_SCHEMES}
                disabled={analysisLoading}
              />
              <Select
                label="字體組合"
                value={fontPair}
                onChange={(e) => setFontPair(e.target.value as FontPair)}
                options={FONT_PAIRS}
                disabled={analysisLoading}
              />
              <Select
                label="佈局風格"
                value={layoutStyle}
                onChange={(e) => setLayoutStyle(e.target.value as LayoutStyle)}
                options={LAYOUT_STYLES}
                disabled={analysisLoading}
              />
            </div>
          </div>

          {analysisError && (
            <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-900/30 text-red-700 dark:text-red-300 text-sm flex items-start gap-2">
              <AlertCircle size={16} className="shrink-0 mt-0.5" />
              <span>{analysisError}</span>
            </div>
          )}

          <button
            type="button"
            onClick={handleSubmit}
            disabled={analysisLoading}
            className="w-full flex items-center justify-center gap-2 px-6 py-3.5 rounded-lg bg-blue-600 hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold text-lg transition-colors"
          >
            <Sparkles size={20} className={analysisLoading ? 'animate-spin' : ''} />
            {analysisLoading ? 'AI 分析中...' : '生成網站方案'}
          </button>
        </div>

        {analysisLoading && (
          <div className="absolute inset-0 z-10 flex flex-col items-center justify-center rounded-xl bg-white/80 dark:bg-slate-800/80 backdrop-blur-sm">
            <Sparkles size={40} className="animate-spin text-blue-600 mb-4" />
            <p className="text-lg font-medium text-slate-900 dark:text-white">AI 正在分析參考網站風格...</p>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">這可能需要數秒至一分鐘，請稍候。</p>
          </div>
        )}
      </div>
    </div>
  );
}
